import { useId, useState } from 'react';
import { ArrowPathIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { RiSave3Line } from 'react-icons/ri';
import Popup from '../../../../../../../components/general/Popup.tsx';
import { PrimaryButton } from '../../../../../../../components/general/PrimaryButton.tsx';
import { SecondaryButton } from '../../../../../../../components/general/SecondaryButton.tsx';
import { Tag } from '../../../../../../../components/general/Tag.tsx';
import { VatDeclarationForm } from '../../../../../../../components/specific/VatDeclaration/index.ts';
import { cn } from '../../../../../../../lib/utils.ts';
import { getVatReturnSaveBlockReason, type VatReturnSaveBlockReason } from '../utils/vatReturnSaveAvailability.ts';
import type { ComputedVatDeclaration } from '../useVatReturns.ts';
import type { VatReturnPeriod } from '../types/index.ts';
import {
    vatReturnPeriodLabel,
    vatReturnPeriodRangeText,
} from '../utils/index.ts';
import { VatReturnCompletenessCard } from './VatReturnCompletenessCard.tsx';
import { VatReturnExcludedDocuments } from './VatReturnExcludedDocuments.tsx';

const SAVE_BLOCK_REASON_MESSAGES: Record<VatReturnSaveBlockReason, string> = {
    not_generated: 'Het concept wordt nog gegenereerd of kon niet worden gegenereerd',
    draft_refreshing: 'Het concept wordt bijgewerkt met de laatste documenten',
    not_month_or_quarter: 'Alleen een volledige maand of kwartaal kan worden opgeslagen',
    period_not_ended: 'Deze periode is nog niet afgelopen',
    approval_status_loading: 'De status van deze periode wordt geladen',
    approval_status_unavailable: 'De status van deze periode kon niet worden geladen. Probeer het later opnieuw',
    awaiting_approval: 'Deze periode wacht op goedkeuring van de klant. Trek het verzoek in bij de nieuwste versie om een nieuwe versie op te slaan',
    approved: 'Deze periode is goedgekeurd door de klant',
};

export interface VatReturnDraftViewProps {
    businessId: string;
    period: VatReturnPeriod;
    declaration: ComputedVatDeclaration | undefined;
    isComputing: boolean;
    isRefreshing: boolean;
    hasComputeError: boolean;
    today: string;
    isLoadingApprovalStatus: boolean;
    hasApprovalStatusLoadError: boolean;
    isSaving: boolean;
    onSave: () => void;
    onRegenerate: () => void;
    onDiscard: () => void;
    onSelectVersion: (snapshotId: string) => void;
}

export function VatReturnDraftView({
    businessId,
    period,
    declaration,
    isComputing,
    isRefreshing,
    hasComputeError,
    today,
    isLoadingApprovalStatus,
    hasApprovalStatusLoadError,
    isSaving,
    onSave,
    onRegenerate,
    onDiscard,
    onSelectVersion,
}: VatReturnDraftViewProps) {
    const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false);
    const headingId = useId();
    const label = vatReturnPeriodLabel(period.periodStart, period.periodEnd);
    const rangeText = vatReturnPeriodRangeText(period.periodStart, period.periodEnd);
    const isVersionKnown = !isLoadingApprovalStatus && !hasApprovalStatusLoadError;
    const saveTitle = isVersionKnown ? `Opslaan als versie ${(period.latestVersion?.version ?? 0) + 1}` : 'Opslaan';
    const shownDeclaration = isComputing || hasComputeError ? undefined : declaration;
    const saveBlockReason = getVatReturnSaveBlockReason({
        hasGeneratedDeclaration: shownDeclaration !== undefined,
        isRefreshingDeclaration: isRefreshing,
        dateFrom: period.periodStart,
        dateTo: period.periodEnd,
        today,
        approvalStatus: period.latestVersion?.approval_status ?? null,
        isLoadingApprovalStatus,
        hasApprovalStatusLoadError,
    });
    const versionAwaitingApproval = saveBlockReason === 'awaiting_approval' ? period.latestVersion : null;

    return (
        <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 id={headingId} className="text-2xl font-semibold text-gray-900">BTW-aangifte {label}</h2>
                        <Tag variant="secondary" className="border-dashed">Concept</Tag>
                    </div>
                    {label !== rangeText && <p className="text-sm text-gray-500">{rangeText}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <SecondaryButton title="Opnieuw genereren" icon={ArrowPathIcon} onClick={onRegenerate} disabled={isComputing || isSaving} />
                    <SecondaryButton title="Concept verwijderen" icon={TrashIcon} onClick={() => setIsDiscardConfirmationOpen(true)} disabled={isSaving} />
                    <PrimaryButton
                        title={isSaving ? 'Opslaan...' : saveTitle}
                        icon={RiSave3Line}
                        onClick={onSave}
                        disabled={saveBlockReason !== null || isSaving}
                        disabledFeedback={saveBlockReason ? SAVE_BLOCK_REASON_MESSAGES[saveBlockReason] : undefined}
                    />
                </div>
            </div>

            {saveBlockReason !== null && !isComputing && (
                <p className="text-right text-sm text-gray-500">
                    {SAVE_BLOCK_REASON_MESSAGES[saveBlockReason]}.
                    {versionAwaitingApproval && (
                        <>
                            {' '}
                            <button
                                type="button"
                                onClick={() => onSelectVersion(versionAwaitingApproval.id)}
                                className="font-semibold text-gray-700 underline underline-offset-2"
                            >
                                Ga naar versie {versionAwaitingApproval.version}
                            </button>
                        </>
                    )}
                </p>
            )}

            <p className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <PencilIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                Concept, niet opgeslagen. Na opslaan staat deze versie vast.
            </p>

            {isComputing && <p role="status" className="text-sm text-gray-500">Concept wordt gegenereerd...</p>}
            {!isComputing && hasComputeError && (
                <p role="alert" className="text-sm text-red-700">Het concept kon niet worden gegenereerd. Probeer het opnieuw.</p>
            )}

            {shownDeclaration && (
                <div className="flex flex-col gap-5">
                    <div className={cn('grid grid-cols-1 gap-4', shownDeclaration.unused_items.length > 0 && 'md:grid-cols-2')}>
                        <VatReturnCompletenessCard
                            documentCount={shownDeclaration.completeness.document_count}
                            matchedCount={shownDeclaration.completeness.matched_count}
                            matchedPercentage={shownDeclaration.completeness.matched_percentage}
                            periodText={`tussen ${vatReturnPeriodRangeText(period.periodStart, period.periodEnd, ' en ')}`}
                        />
                        <VatReturnExcludedDocuments businessId={businessId} unusedItems={shownDeclaration.unused_items} />
                    </div>
                    <VatDeclarationForm businessId={businessId} vatDeclaration={shownDeclaration} />
                </div>
            )}

            <Popup
                open={isDiscardConfirmationOpen}
                onClose={() => setIsDiscardConfirmationOpen(false)}
                onConfirm={() => {
                    setIsDiscardConfirmationOpen(false);
                    onDiscard();
                }}
                title="Concept verwijderen?"
                confirmButtonDisplayLabel="Verwijderen"
                variant="danger"
            >
                <p className="text-sm text-gray-600">
                    Het concept voor {label} is niet opgeslagen. Als je het verwijdert, is het weg. Opgeslagen versies blijven bestaan.
                </p>
            </Popup>
        </section>
    );
}
